import React from "react";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Payment & Refund Policy
          </h1>

          <div className="text-sm text-gray-600 mb-8 text-center">
            <strong>Effective Date: 01/10/2025</strong>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Promise
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We aim to make paying for our services simple and transparent.
                This page explains how payments work, when refunds apply, and
                what's excluded—so there are no surprises.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Who Can Pay Online
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Franchisees, partner associates, and clients can complete
                payments for Education Planner services through our website and
                web application.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                How Payments Are Handled
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Payments are processed through trusted payment gateways. You'll
                see a confirmation on screen (and/or by email) once a
                transaction is successful.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                When a Payment Might Not Go Through
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A payment can fail even when you've done everything right.
                Common reasons include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Incomplete or incorrect details:</strong> Typos,
                  expired cards, or late/invalid instructions.
                </li>
                <li>
                  <strong>Insufficient balance or limits:</strong> Not enough
                  funds or a bank-imposed cap.
                </li>
                <li>
                  <strong>Holds or encumbrances:</strong> The bank has placed a
                  lien/hold on your account.
                </li>
                <li>
                  <strong>Bank or clearing delays:</strong> Your bank or the
                  clearing network rejects or slows the payment.
                </li>
                <li>
                  <strong>Receiver-side delays:</strong> The biller doesn't pick
                  up/process the payment immediately.
                </li>
                <li>
                  <strong>Events outside anyone's control:</strong>{" "}
                  Power/network outages, system failures, natural disasters,
                  strikes, etc.
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                If your payment isn't completed, we'll show a failure notice on
                the payment page and/or notify you using your registered contact
                details.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Refunds on Application Fees (Very Limited)
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Application fees are refundable only in these two scenarios:
              </p>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Ineligibility</strong> – The student does not meet the
                  stated eligibility criteria of the chosen institution/program.
                </li>
                <li>
                  <strong>Program closed</strong> – The program is closed for
                  the intended intake at the time the application is being
                  processed.
                </li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                How Refunds Are Issued
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  Refunds are sent back to the original payment method/account.
                </li>
                <li>Gateway/processing charges (if any) are deducted.</li>
                <li>
                  Once approved, refunds are typically credited within 10
                  working days.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Non-Refundable Fees
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Payments made for Education Planner Learning Programs (our
                training/learning offerings) are non-refundable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                A Few Definitions (Plain Language)
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Application fee:</strong> The fee charged to submit
                  your application to an institution.
                </li>
                <li>
                  <strong>Processing/ gateway charges:</strong> Third-party
                  costs from banks/payment processors.
                </li>
                <li>
                  <strong>Working days:</strong> Monday–Friday, excluding public
                  holidays in our operating jurisdiction.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Need Help?
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If you're unsure about a charge, a failed payment, or
                eligibility for a refund, reach out:
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                📧{" "}
                <a
                  href="mailto:support@educationplanner.io"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  support@educationplanner.io
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
